import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare,
  Phone,
  Mail,
  Car,
  DollarSign,
  Clock,
  Plus,
  X,
  User,
  ArrowRight,
  Bot,
  TrendingUp,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { leadService } from '../../services/api';
import {
  getDisplayName,
  formatBudget,
  SOURCE_CONFIG,
  KANBAN_STATUSES,
  STATUS_COLORS,
  INTENT_CONFIG,
} from './leadUtils';

function InfoRow({ icon: Icon, label, value, children }) {
  if (!value && !children) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div>
        <span className="text-slate-500 text-xs">{label}</span>
        <div className="text-slate-800 font-medium">{children || value}</div>
      </div>
    </div>
  );
}

export default function LeadDetailPanel({ lead, open, onClose, onLeadUpdated }) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [agentActive, setAgentActive] = useState(lead?.agent_active ?? false);
  const [togglingAgent, setTogglingAgent] = useState(false);

  useEffect(() => {
    setAgentActive(lead?.agent_active ?? false);
  }, [lead?.id, lead?.agent_active]);

  if (!lead) return null;

  const customer = lead.customer || {};
  const displayName = getDisplayName(customer);
  const source = SOURCE_CONFIG[lead.source] || SOURCE_CONFIG.manual;
  const SourceIcon = source.icon;

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const updated = await leadService.update(lead.id, { status: newStatus });
      onLeadUpdated(updated);
      toast.success('Estado actualizado');
    } catch {
      toast.error('Error al actualizar estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const updated = await leadService.addNote(lead.id, newNote.trim());
      onLeadUpdated(updated);
      setNewNote('');
      toast.success('Nota agregada');
    } catch {
      toast.error('Error al agregar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag) return;
    const currentTags = lead.tags || [];
    if (currentTags.includes(tag)) {
      setNewTag('');
      return;
    }
    try {
      const updated = await leadService.updateTags(lead.id, [...currentTags, tag]);
      onLeadUpdated(updated);
      setNewTag('');
    } catch {
      toast.error('Error al agregar etiqueta');
    }
  };

  const handleAgentToggle = async (newValue) => {
    setAgentActive(newValue);
    setTogglingAgent(true);
    try {
      const updated = await leadService.toggleAgent(lead.id, newValue);
      onLeadUpdated(updated);
      toast.success(newValue ? 'Agente IA activado' : 'Agente IA desactivado');
    } catch {
      setAgentActive(!newValue);
      toast.error('Error al cambiar el agente');
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleRemoveTag = async (tag) => {
    const updatedTags = (lead.tags || []).filter((t) => t !== tag);
    try {
      const updated = await leadService.updateTags(lead.id, updatedTags);
      onLeadUpdated(updated);
    } catch {
      toast.error('Error al eliminar etiqueta');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-xl">{displayName}</SheetTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={STATUS_COLORS[lead.status] || 'bg-slate-100 text-slate-600'}>
              {KANBAN_STATUSES.find((s) => s.value === lead.status)?.label || lead.status}
            </Badge>
            <Badge variant="outline" className={source.textColor}>
              <SourceIcon className="w-3 h-3 mr-1" />
              {source.label}
            </Badge>
            <span className="text-xs text-slate-400">
              {lead.createdAt
                ? formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true, locale: es })
                : ''}
            </span>
          </div>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Cliente
            </h3>
            <InfoRow icon={User} label="Nombre" value={displayName} />
            <InfoRow icon={Phone} label="Teléfono" value={customer.phone}>
              {customer.phone && (
                <div className="flex items-center gap-2">
                  <span>{customer.phone}</span>
                  <a
                    href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full hover:bg-green-200 transition-colors"
                  >
                    <MessageSquare className="w-3 h-3" />
                    WhatsApp
                  </a>
                </div>
              )}
            </InfoRow>
            <InfoRow icon={Mail} label="Email" value={customer.email} />
            <InfoRow icon={DollarSign} label="Presupuesto">
              {formatBudget(customer.budget)}
            </InfoRow>
          </section>

          <Separator />

          {(lead.vehicleInterest?.make || lead.vehicleInterest?.model) && (
            <>
              <section className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Vehículo de interés
                </h3>
                <InfoRow icon={Car} label="Marca / Modelo">
                  {`${lead.vehicleInterest?.make || ''} ${lead.vehicleInterest?.model || ''}`.trim()}
                  {lead.vehicleInterest?.year && (
                    <span className="text-slate-400 ml-1">
                      ({lead.vehicleInterest.year})
                    </span>
                  )}
                </InfoRow>
              </section>
              <Separator />
            </>
          )}

          {(lead.vehicleInterestText || lead.budgetText || lead.intent) && (
            <>
              <section className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                  <Bot className="w-3 h-3" />
                  Análisis del agente IA
                </h3>
                {lead.vehicleInterestText && (
                  <InfoRow icon={Car} label="Vehículo de interés" value={lead.vehicleInterestText} />
                )}
                {lead.budgetText && (
                  <InfoRow icon={DollarSign} label="Presupuesto mencionado" value={lead.budgetText} />
                )}
                {lead.intent && INTENT_CONFIG[lead.intent] && (
                  <div className="flex items-start gap-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-slate-500 text-xs">Intención de compra</span>
                      <div className="mt-0.5">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${INTENT_CONFIG[lead.intent].className}`}
                        >
                          {INTENT_CONFIG[lead.intent].label}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </section>
              <Separator />
            </>
          )}

          {lead.message && (
            <>
              <section className="space-y-1">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Mensaje
                </h3>
                <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">
                  {lead.message}
                </p>
              </section>
              <Separator />
            </>
          )}

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Estado
            </h3>
            <Select
              value={lead.status}
              onValueChange={handleStatusChange}
              disabled={updatingStatus}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KANBAN_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <Separator />

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Agente IA
            </h3>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 bg-slate-50">
              <div className="flex items-center gap-2">
                <Bot className={`w-4 h-4 ${agentActive ? 'text-violet-600' : 'text-slate-400'}`} />
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {agentActive ? 'Agente activo' : 'Agente inactivo'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {agentActive
                      ? 'El bot está respondiendo esta conversación'
                      : 'El bot está pausado para esta conversación'}
                  </p>
                </div>
              </div>
              <Switch
                checked={agentActive}
                onCheckedChange={handleAgentToggle}
                disabled={togglingAgent}
              />
            </div>
            {lead.chatwoot_conversation_id && (
              <p className="text-xs text-slate-400">
                Conversación Chatwoot #{lead.chatwoot_conversation_id}
              </p>
            )}
          </section>

          <Separator />

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Etiquetas
            </h3>
            <div className="flex flex-wrap gap-1 min-h-[24px]">
              {(lead.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Nueva etiqueta..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={handleAddTag} className="h-8">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </section>

          <Separator />

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Notas internas
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(lead.notes || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">Sin notas todavía.</p>
              )}
              {[...(lead.notes || [])].reverse().map((note, i) => (
                <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                  <p className="text-sm text-slate-700">{note.text}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {note.createdAt
                      ? format(new Date(note.createdAt), "d MMM yyyy 'a las' HH:mm", {
                          locale: es,
                        })
                      : ''}
                  </p>
                </div>
              ))}
            </div>
            <Textarea
              placeholder="Escribí una nota interna..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={2}
              className="text-sm resize-none"
            />
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={addingNote || !newNote.trim()}
              className="w-full"
            >
              <Plus className="w-3 h-3 mr-1" />
              Agregar nota
            </Button>
          </section>

          <Separator />

          {(lead.statusHistory || []).length > 0 && (
            <>
              <section className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Historial de estados
                </h3>
                <div className="space-y-2">
                  {[...(lead.statusHistory || [])].reverse().map((entry, i) => {
                    const toLabel =
                      KANBAN_STATUSES.find((s) => s.value === entry.toStatus)?.label ||
                      entry.toStatus;
                    const fromLabel = entry.fromStatus
                      ? KANBAN_STATUSES.find((s) => s.value === entry.fromStatus)?.label ||
                        entry.fromStatus
                      : null;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3 shrink-0" />
                        <span>
                          {entry.changedAt
                            ? format(new Date(entry.changedAt), 'd MMM yyyy HH:mm', { locale: es })
                            : ''}
                        </span>
                        {fromLabel && (
                          <>
                            <span className="text-slate-400">{fromLabel}</span>
                            <ArrowRight className="w-3 h-3" />
                          </>
                        )}
                        <span className="font-medium text-slate-700">{toLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
              <Separator />
            </>
          )}

          <section className="space-y-2 pb-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" />
              Conversación
            </h3>
            {lead.conversation && lead.conversation.length > 0 ? (
              <div className="bg-slate-100 rounded-xl p-3 space-y-2 max-h-96 overflow-y-auto">
                {lead.conversation.map((msg, i) => {
                  const isAgent = msg.role === 'agent';
                  const time = msg.timestamp
                    ? format(new Date(msg.timestamp), 'HH:mm')
                    : '';
                  return (
                    <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                          isAgent
                            ? 'bg-blue-500 text-white rounded-br-none'
                            : 'bg-white text-slate-800 rounded-bl-none'
                        }`}
                      >
                        <p className="leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                        {time && (
                          <p
                            className={`text-[10px] mt-1 text-right ${
                              isAgent ? 'text-blue-200' : 'text-slate-400'
                            }`}
                          >
                            {time}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Sin conversación registrada.</p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
